import { getYouTubeThumbnail } from './youtube';
import { getCloudFrontImageUrl } from './cloudFrontImage';
import { restoreImageSlots } from './postImageSlots';
import i18n from '../i18n';

const strVal = (v: unknown): string => (typeof v === 'string' ? v.trim() : '');

/** API snake_case / camelCase 및 본문 슬롯 복원까지 반영해 첫 이미지 src 추출 (카카오 썸네일용) */
const firstImageFromRestoredPostBody = (post: Record<string, unknown> | undefined): string => {
  if (!post) return '';
  const html = strVal(post.update_message ?? post.updateMessage ?? post.contents);
  if (!html) return '';
  try {
    const restored = restoreImageSlots(html, post);
    const doc = new DOMParser().parseFromString(restored, 'text/html');
    const storyImg = doc.querySelector('figure[data-embed="story-image"] img');
    const fromStory = storyImg?.getAttribute('src')?.trim();
    if (fromStory) return fromStory;
    return doc.querySelector('img[src]')?.getAttribute('src')?.trim() || '';
  } catch {
    return '';
  }
};

type KakaoShareFeedOptions = {
  url: string;
  title: string;
  description?: string;
  imageUrl?: string;
  buttonTitle?: string;
  /** 모금자명 - 프로젝트/소식 공유 시 전달 문구에 사용 */
  fundraiserName?: string;
  /** 민감지역(restricted_region) 사용자 여부 - true면 설명에 창의적 접근지역 문구 사용 */
  restrictedRegion?: boolean;
  /**
   * 새소식 공유인지 명시 (링크에 postId 쿼리가 없을 때도 새소식 카피를 쓰려면 'post')
   * 미지정 시 URL의 postId 쿼리 유무로 추론
   */
  feedKind?: 'post' | 'project';
};

/** 프로젝트 공유용 썸네일 URL - 유튜브 URL 기반 파싱 우선 > 이미지 > 기본 이미지 순으로 반환 */
export const getProjectShareImageUrl = (project?: {
  image_url?: string;
  video_url?: string;
  imageUrl?: string;
  videoUrl?: string;
} | null): string => {
  const p = project as Record<string, unknown> | undefined;
  const videoRaw = strVal(p?.video_url ?? p?.videoUrl);
  const imageRaw = strVal(p?.image_url ?? p?.imageUrl);

  // 유튜브 비디오: 서버 image_url 대신 video_url 기반으로 파싱
  const yt = getYouTubeThumbnail(videoRaw, 'hqdefault');
  if (yt) return yt;

  const img = imageRaw;
  // base64·blob 등 카카오가 가져올 수 없는 값은 무시 (민감지역 여부와 무관)
  const looksLikeShareableImage =
    img &&
    !/\.(mp4|webm|mov|avi)(\?|$)/i.test(img) &&
    (/^https?:\/\//i.test(img) || img.startsWith('/OMF/'));
  if (looksLikeShareableImage) {
    return getCloudFrontImageUrl(img, '800x0') || img;
  }
  return resolveFallbackShareImageUrl();
};

/** 소식(포스트) 공유용 썸네일 URL - 대표·슬롯 이미지 우선 → 본문(슬롯 복원) → 유튜브 → 프로젝트 썸네일 */
export const getPostShareImageUrl = (
  post?: { video_url?: string; image_url?: string; image2_url?: string; image3_url?: string; image4_url?: string; image5_url?: string; contents?: string; update_message?: string } | null,
  project?: { image_url?: string; video_url?: string; imageUrl?: string; videoUrl?: string } | null,
  firstImageInContents?: string,
): string => {
  const p = post as Record<string, unknown> | undefined;
  const fromRestored = firstImageFromRestoredPostBody(p);
  const inline = strVal(firstImageInContents) || fromRestored;

  const postImg =
    strVal(p?.image_url ?? p?.imageUrl) ||
    strVal(p?.image2_url ?? p?.image2Url) ||
    strVal(p?.image3_url ?? p?.image3Url) ||
    strVal(p?.image4_url ?? p?.image4Url) ||
    strVal(p?.image5_url ?? p?.image5Url) ||
    inline;

  if (postImg && !/\.(mp4|webm|mov|avi)(\?|$)/i.test(postImg)) {
    return getCloudFrontImageUrl(postImg, '800x0') || postImg;
  }

  const postVid = strVal(p?.video_url ?? p?.videoUrl);
  const postYt = getYouTubeThumbnail(postVid, 'hqdefault');
  if (postYt) return postYt;

  const proj = project as Record<string, unknown> | undefined;
  const projectImg = strVal(proj?.image_url ?? proj?.imageUrl);
  if (projectImg && !/\.(mp4|webm|mov|avi)(\?|$)/i.test(projectImg)) {
    return getCloudFrontImageUrl(projectImg, '800x0') || projectImg;
  }
  const projectYt = getYouTubeThumbnail(strVal(proj?.video_url ?? proj?.videoUrl), 'hqdefault');
  if (projectYt) return projectYt;

  return resolveFallbackShareImageUrl();
};

const LEGACY_FALLBACK_SHARE_IMAGE_URL =
  'https://mfimage.s3.amazonaws.com/OMF/projects/6926946aa11b995e56a08516_1764136042390.png';

const normalizeUrl = (url: string) =>
  url.replace(/^http:\/\//i, 'https://').replace(/:\/\/go\.missionfund\.org/i, '://go.missionfund.org');

const sanitizeText = (value: string) =>
  value
    .replace(/\[\[IMG_SLOT_\d+\]\]/g, ' ')
    .replace(/<br\s*\/?>/gi, ' ')
    .replace(/<[^>]*>/g, ' ')
    .replace(/\*/g, '')
    .replace(/\s+/g, ' ')
    .trim();

export const resolveFallbackShareImageUrl = () => {
  // 카카오 서버 크롤러 접근 안정성을 위해 CloudFront 절대 URL 사용
  return getCloudFrontImageUrl(LEGACY_FALLBACK_SHARE_IMAGE_URL, '800x0') || LEGACY_FALLBACK_SHARE_IMAGE_URL;
};

const normalizeImageUrl = (imageUrl?: string) => {
  let trimmed = String(imageUrl ?? '').trim();
  if (!trimmed) return resolveFallbackShareImageUrl();
  trimmed = trimmed.replace(/^http:\/\//i, 'https://');
  if (!/^https:\/\//i.test(trimmed)) return resolveFallbackShareImageUrl();
  return trimmed;
};

export const shareKakaoFeed = (kakao: any, options: KakaoShareFeedOptions) => {
  const sendDefault = kakao?.Share?.sendDefault ?? kakao?.Link?.sendDefault;
  if (!sendDefault) {
    throw new Error('Kakao share API is unavailable');
  }

  const normalizedUrl = normalizeUrl(options.url);
  const isPostShare =
    options.feedKind === 'post' || (options.feedKind !== 'project' && /[?&]postId=/.test(normalizedUrl));
  const fundraiserName = sanitizeText(options.fundraiserName || '') || i18n.t('common.share.fundraiserFallback');
  const fundraiserDisplay = `[${fundraiserName}]`;
  const isRestricted = options.restrictedRegion === true;

  const title = isRestricted && !isPostShare
    ? i18n.t('common.share.restrictedProjectTitle')
    : isRestricted && isPostShare
      ? i18n.t('common.share.restrictedPostTitle')
      : isPostShare
        ? i18n.t('common.share.postTitle', { fundraiser: fundraiserDisplay })
        : (sanitizeText(options.title) || i18n.t('common.shareModal.defaultTitle'));
  const description = isRestricted && !isPostShare
    ? i18n.t('common.share.restrictedProjectDescription')
    : isRestricted && isPostShare
      ? i18n.t('common.share.postDescription')
      : isPostShare
        ? i18n.t('common.share.postDescription')
        : i18n.t('common.share.projectDescription', { fundraiser: fundraiserDisplay });
  const imageUrl = normalizeImageUrl(options.imageUrl);
  const buttonTitle = sanitizeText(options.buttonTitle || '') || i18n.t('common.shareModal.buttonDetail');

  sendDefault({
    objectType: 'feed',
    content: {
      title,
      description,
      imageUrl,
      imageWidth: 800,
      imageHeight: 550,
      link: {
        mobileWebUrl: normalizedUrl,
        webUrl: normalizedUrl,
      },
    },
    buttons: [
      {
        title: buttonTitle,
        link: {
          mobileWebUrl: normalizedUrl,
          webUrl: normalizedUrl,
        },
      },
    ],
  });
};

